import { Component, computed, inject, input, output } from '@angular/core';
import { select } from '@ngxs/store';
import { InventoryManagementBidsFilter, InventoryManagementState, LaunchDarklyService } from 'remarketing-angular-library';
import { translateObjectSignal } from '@jsverse/transloco';
import { SaComponent } from 'smartauction-frontend-micro-library';
import { MatRadioModule } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import { AccountManagerDealerFilterComponent } from "../../account-manager/account-manager-dealer-filter/account-manager-dealer-filter.component";
import { AccountManagerEmployeeAggregation } from 'src/inventory-management/enums/account-manager.enum';

export interface BidTypeOption {
  id: string;
  name: string;
  value: string;
  dataTrackName: string;
  ariaLabel: string;
}

@Component({
  selector: 'app-bids-filter',
  imports: [MatRadioModule, FormsModule, AccountManagerDealerFilterComponent],
  templateUrl: './bids-filter.component.html',
  styleUrls: ['./bids-filter.component.scss']
})
export class BidsFilterComponent extends SaComponent {
  // Inputs
  label = input<string>('');

  // Outputs
  bidFilterSelection = output<InventoryManagementBidsFilter>();

  // Attributes
  protected readonly inventoryManagementBidsFilter = InventoryManagementBidsFilter;
  protected readonly accountManagerEmployeeAggregation = AccountManagerEmployeeAggregation;

  // DI Services
  private readonly launchDarklyService: LaunchDarklyService;

  // Signals
  private readonly inventoryManagementSearchCriteria$ = select(InventoryManagementState.searchCriteria);
  private readonly translations$ = translateObjectSignal(['sellModule.manageInventory.searchFilter']);

  // Computed Signals
  protected readonly bidFilterLabel = computed(() => {
    return this.label() || this.translations$()[0]?.labelBids || 'Bids';
  });

  protected readonly bidTypes = computed((): BidTypeOption[] => {
    const bidTranslations = this.translations$()[0]?.radioBtnGrpBids;
    return [
      {
        id: 'active-filter-all-bids',
        name: bidTranslations?.all || 'All',
        value: undefined,
        dataTrackName: 'active-filter-all-bids',
        ariaLabel: bidTranslations?.all || 'All'
      },
      {
        id: 'active-filter-active-bids',
        name: bidTranslations?.activeBids || 'Active Bids',
        value: InventoryManagementBidsFilter.WITH_BIDS,
        dataTrackName: 'active-filter-active-bids',
        ariaLabel: bidTranslations?.activeBids || 'Active Bids'
      },
      {
        id: 'active-filter-no-bids',
        name: bidTranslations?.noBids || 'No Bids',
        value: InventoryManagementBidsFilter.WITHOUT_BIDS,
        dataTrackName: 'active-filter-no-bids',
        ariaLabel: bidTranslations?.noBids || 'No Bids'
      }
    ];
  });

  protected readonly selectedBidFilter = computed(() => {
    return this.inventoryManagementSearchCriteria$().filterByBids;
  });

  // Getters
  get isSimEmployeeFilterEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sim-employeeFilterEnabled', false);
  }

  // Show account manager dropdowns when the Account Manager feature is enabled and Active Bids is selected. Show dropdowns under active bids radio
  showAccountManagerDropdowns(bidType: InventoryManagementBidsFilter): boolean {
    return this.isSimEmployeeFilterEnabled 
      && bidType === InventoryManagementBidsFilter.WITH_BIDS 
      && this.selectedBidFilter() === InventoryManagementBidsFilter.WITH_BIDS;
  }

  constructor() {
    super()
    this.launchDarklyService = inject(LaunchDarklyService);
  }

  onBidFilterChange(bidFilter: InventoryManagementBidsFilter): void {
    if (this.selectedBidFilter() !== bidFilter) {
      this.bidFilterSelection.emit(bidFilter);
    }
  }
}
